import React from "react";
import { Title, Text, Paper, Stack } from "@mantine/core";
import type { Section } from "@/pages/ClientProfile/types";
import { chunkItems, flattenContent } from "@/pages/ClientProfile/utils";
import LabelItem from "../items/LabelItem";
import ReviewItem from "../items/ReviewItem";
import DocumentItem from "../items/DocumentItem";
import DynamicItemsField from "../items/DynamicItemsField";
import { useProfileStore } from "@/pages/ClientProfile/store/profileStore";
import DocumentScanFailed from "../../EmptyState/DocumentScanFailed";

interface SectionContentProps {
  section: Section;
  sectionRef?: React.RefObject<HTMLDivElement> | null;
  contentData: Record<string, any>;
  isVeryFirstPage: boolean;
  classes: Record<string, string>;
}

export const SectionContent: React.FC<SectionContentProps> = ({
  section,
  sectionRef,
  contentData,
  isVeryFirstPage,
  classes,
}) => {
  const { headerData, activeYear } = useProfileStore();
  const { review, selectedDoc } = useProfileStore();

  // Get form type from the content data
  const formType = contentData?.owner?.type || null;

  const sectionData = contentData[section.key];
  const allItems = sectionData ? flattenContent(sectionData, 0, formType) : [];
  const pages = chunkItems(allItems);

  // Construct full name with proper fallback logic
  const firstName = headerData?.basicInfo?.firstName;
  const lastName = headerData?.basicInfo?.lastName;
  const fullName =
    firstName || lastName
      ? `${firstName ?? ""} ${lastName ?? ""}`.trim()
      : "Client";

  if (allItems.length === 0) {
    if (review) {
      return <DocumentScanFailed docId={selectedDoc?.docId ?? null} />;
    }
    return (
      <>
        <Text c="dimmed" size="sm" className={classes.sectionLabel}>
          {section.label}
        </Text>
        <Paper withBorder className={classes.emptyPaper}>
          <Title order={2} style={{ marginBottom: "0.5rem" }}>
            {section.label}
          </Title>
          <Text size="sm" className={classes.italicText}>
            No data available.
          </Text>
        </Paper>
      </>
    );
  }

  return (
    <div
      ref={sectionRef}
      key={section.key}
      style={{ width: "100%", overflow: "hidden" }}
    >
      {pages.map((pageItems, pageIndex) => {
        const isFirstPage = pageIndex === 0;
        const displayLabel = isFirstPage
          ? section.label
          : `${section.label} (Continued)`;

        const showWelcome = isVeryFirstPage && pageIndex === 0;

        return (
          <React.Fragment key={`${section.key}-page-${pageIndex}`}>
            <Text
              c="dimmed"
              size="sm"
              ml={{ base: "10px", sm: "12px" }}
              className={
                isFirstPage ? classes.sectionLabel : classes.continuedLabel
              }
            >
              {displayLabel}
            </Text>
            <Paper withBorder className={classes.paper}>
              {showWelcome && (
                <Stack className={classes.fieldHeader}>
                  <Text fw={500}>{activeYear} Tax Organizer (1040)</Text>
                  <Text fw={500}>{fullName}</Text>
                </Stack>
              )}
              <div className={classes.grid}>
                {pageItems.map((item) => {
                  const gridColumnSpan =
                    item.type === "label" || item.type === "dynamic_items"
                      ? "1 / -1"
                      : "span 1";

                  return (
                    <div
                      key={item.key}
                      style={{
                        gridColumn: gridColumnSpan,
                        width: "100%",
                      }}
                    >
                      {item.type === "label" && <LabelItem item={item} />}
                      {item.type === "field" && <ReviewItem item={item} />}
                      {item.type === "document" && <DocumentItem item={item} />}
                      {item.type === "dynamic_items" && (
                        <DynamicItemsField
                          item={item}
                          formType={formType}
                          fieldKey={item.key.split(".")[2] || ""}
                          classes={classes}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </Paper>
          </React.Fragment>
        );
      })}
    </div>
  );
};
